import React, { useEffect, useRef, useState } from 'react';
import { formatTime } from './utils';
// Import types for settings and state
// If the actual type imports don't match this format, adjust accordingly
import { CPRSettings } from '../CPRSettings';
import { CPRState } from '../CprState/CPRStateContext';
import { useTranslation } from 'react-i18next';

interface TimerDisplayProps {
  state: CPRState;
  settings: CPRSettings;
}

export const TimerDisplay: React.FC<TimerDisplayProps> = ({ state, settings }) => {
  const { t } = useTranslation(['resus']);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (state.isRunning) {
      // Update every second for the timer display only
      timerRef.current = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state.isRunning]);

  // Calculate elapsed time for timer display
  const endTime: number = Math.max(state.endTime ? state.endTime : currentTime, state.startTime);
  const elapsedSeconds = state.startTime ? Math.floor((endTime - state.startTime) / 1000) : 0;
  
  // Calculate display timer
  const displayTimer = getDisplayTimer(endTime, state, settings);

  return (
    <div className="timer-display">
      <div className="elapsed-time" data-testid="cpr-manager-elapsed-time">
        {formatTime(elapsedSeconds)}
      </div>
      {settings.timerDisplay !== 'none' && (
        <>
          <div className="countdown-label">
            {settings.timerDisplay === 'adrenaline' && t('cprTimer.adrenalineDueIn')}
            {settings.timerDisplay === 'massager' && t('cprTimer.massagerDueIn')}
          </div>
          <div className={`countdown-time ${displayTimer === 0 ? 'zero' : ''}`}>
            {formatTime(displayTimer)}
          </div>
        </>
      )}
    </div>
  );
};

// Helper function moved from original component
function getDisplayTimer(endTime: number, state: CPRState, settings: CPRSettings): number {
  if (settings.timerDisplay === 'massager' && settings.massagerAlertEnabled) {
    const timeSinceLastMassager = endTime - state.lastMassagerTime;
    const massagerIntervalMs = settings.massagerAlertSeconds * 1000;
    return Math.max(0, Math.ceil((massagerIntervalMs - timeSinceLastMassager) / 1000));
  } else if (settings.timerDisplay === 'adrenaline' && settings.adrenalineAlertEnabled) {
    const timeSinceLastAdrenaline = endTime - state.lastAdrenalineTime;
    const adrenalineIntervalMs = settings.adrenalineAlertSeconds * 1000;
    return Math.max(0, Math.ceil((adrenalineIntervalMs - timeSinceLastAdrenaline) / 1000));
  }
  return 0;
}
